// The case itself — one component, rendered two ways.
//
//   variant="pane": docked beside the dashboard / cases list. Scrolls inside
//     its own box, swaps in place when the host changes `caseId` (key it on
//     the id so drafts reset), paints from the list row (`seed`) on the first
//     frame so a click never shows a bare "Loading…".
//   variant="page": the full /cases/:id page — same body, no height cap,
//     reachable from emails and from the pane's "Open page" button.
//
// Chauncey, 2026-10-15: "when I click on the case it opens up to an entirely
// new page which is overkill, it should just open up on the right and be able
// to service the case this way." Everything the page could do, the pane can:
// reply (public or 🔒 internal), attach, change status/type/priority, assign,
// merge, reopen, accept/decline a feature request, verify a fix.
//
// Thread model (unchanged from the 2026-07-01 conversation redesign): the
// original report pinned on top, then corroborations, replies and status
// changes interleaved oldest-first; minor bookkeeping collapses into "n minor
// changes". Reporter entries render neutral; dev-side entries carry the navy
// edge; AI entries the purple edge; internal notes the amber edge.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import {
  QC_CASE_PRIORITIES,
  QC_CASE_STATUSES,
  QC_CASE_TYPES,
  QC_TERMINAL_STATUSES,
  type QcCase,
  type QcCaseActivity,
  type QcCaseDetail,
  type QcCaseNote,
  type QcCasePriority,
  type QcCaseStatus,
  type QcCaseType,
  type QcIncident,
} from '@itms-qc/shared';
import { api, uploadAttachments, type UpdateCasePatch } from '../lib/api';
import { useApiToken } from '../auth/useApiToken';
import { useMe } from '../lib/me';
import { Modal } from './Modal';
import { IncidentForm } from './IncidentForm';
import { AttachmentPicker, appendFiles, type PickedFile } from './AttachmentPicker';
import { logCaseView } from './RecentCasesDropdown';
import { ModuleBadge, PriorityPill, StatusPill, STATUS_LABELS, STATUS_STYLES, TypeIcon } from './badges';
import { absoluteTime, actorLabel, isAiVia, personLabel, relativeTime } from '../lib/format';
import { lastSeenAt, markCaseSeen } from '../lib/seen';
import {
  dangerTextStyle,
  inputStyle,
  primaryButtonStyle,
  secondaryButtonStyle,
  selectStyle,
  textareaStyle,
} from './form';

const MONO = 'var(--font-mono, ui-monospace, SFMono-Regular, Menlo, monospace)';

const cardStyle: React.CSSProperties = {
  background: 'var(--brand-card-bg, white)',
  border: '1px solid var(--brand-border, #D6E4EE)',
  borderRadius: 8,
  padding: '16px 20px',
};

const sectionHeadStyle: React.CSSProperties = {
  margin: '0 0 10px',
  fontSize: 12,
  fontWeight: 600,
  color: 'var(--brand-text-muted, #5A6B7B)',
};

const attachmentLinkStyle: React.CSSProperties = {
  background: 'transparent',
  border: 'none',
  padding: 0,
  cursor: 'pointer',
  fontSize: 12,
  color: 'var(--brand-primary, #001C3E)',
  textDecoration: 'underline',
};

const quietButtonStyle: React.CSSProperties = {
  ...secondaryButtonStyle,
  fontSize: 12,
  padding: '5px 10px',
  background: 'var(--brand-card-bg, white)',
};

const compactSelectStyle: React.CSSProperties = {
  ...selectStyle,
  width: 'auto',
  fontSize: 12,
  padding: '5px 8px',
  borderRadius: 6,
};

// Small "AI" chip shown before an actor name when the action was performed by
// Claude Code on someone's behalf (vs the person doing it themselves).
function AiTag(): React.ReactElement {
  return (
    <span
      title="Performed by an AI agent (Claude Code) on the person's behalf"
      style={{
        fontSize: 9,
        fontWeight: 700,
        letterSpacing: '0.04em',
        color: '#5B21B6',
        background: '#EDE9FE',
        borderRadius: 6,
        padding: '1px 5px',
        marginRight: 6,
        verticalAlign: 'middle',
      }}
    >
      AI
    </span>
  );
}

// Who's speaking: "Reporter" for the opener/corroborators of THIS case,
// "Dev" for anyone else who writes in the thread.
function RoleTag({ role }: { role: 'reporter' | 'dev' }): React.ReactElement {
  const dev = role === 'dev';
  return (
    <span
      style={{
        fontSize: 9,
        fontWeight: 700,
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
        color: dev ? 'var(--brand-text-on-primary, white)' : 'var(--brand-text-muted, #8BA4B8)',
        background: dev ? 'var(--brand-primary, #001C3E)' : 'transparent',
        border: dev ? 'none' : '1px solid var(--brand-border, #D6E4EE)',
        borderRadius: 6,
        padding: '1px 6px',
        verticalAlign: 'middle',
      }}
    >
      {dev ? 'Dev' : 'Reporter'}
    </span>
  );
}

// === The unified thread model ===
type ThreadEntry =
  | { kind: 'incident'; at: string; incident: QcIncident }
  | { kind: 'note'; at: string; note: QcCaseNote }
  | { kind: 'system'; at: string; activity: QcCaseActivity }
  | { kind: 'changes'; at: string; items: QcCaseActivity[] };

const MAJOR_EVENTS = new Set(['status_changed', 'reopened', 'merged', 'archived']);

function buildThread(detail: QcCaseDetail, originalIncidentId: number | undefined): ThreadEntry[] {
  const raw: ThreadEntry[] = [];
  for (const i of detail.incidents) {
    if (i.id !== originalIncidentId) raw.push({ kind: 'incident', at: i.createdAt, incident: i });
  }
  for (const n of detail.notes) raw.push({ kind: 'note', at: n.createdAt, note: n });
  for (const a of detail.activity) {
    if (a.eventType === 'created') continue; // the pinned report IS the opening
    if (MAJOR_EVENTS.has(a.eventType)) {
      raw.push({ kind: 'system', at: a.createdAt, activity: a });
    } else {
      raw.push({ kind: 'changes', at: a.createdAt, items: [a] });
    }
  }
  raw.sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime());

  // Merge CONSECUTIVE minor changes into one expander.
  const out: ThreadEntry[] = [];
  for (const e of raw) {
    const prev = out[out.length - 1];
    if (e.kind === 'changes' && prev?.kind === 'changes') {
      prev.items.push(...e.items);
      prev.at = e.at;
    } else {
      out.push(e);
    }
  }
  return out;
}

function minorChangeLine(a: QcCaseActivity): string {
  const d = a.eventData ?? {};
  const oldVal = 'old' in d ? String(d.old) : null;
  const newVal = 'new' in d ? String(d.new) : null;
  switch (a.eventType) {
    case 'priority_changed':
      return oldVal && newVal ? `priority ${oldVal} → ${newVal}` : 'changed priority';
    case 'assigned':
      return 'changed assignment';
    case 'updated': {
      const fields = Array.isArray(d.fields) ? (d.fields as string[]).join(', ') : null;
      return fields ? `edited ${fields}` : 'edited the case';
    }
    default:
      return a.eventType.replace(/_/g, ' ');
  }
}

export interface CaseViewProps {
  caseId: number;
  variant: 'page' | 'pane';
  /** The list row the host already holds — painted on the first frame. */
  seed?: QcCase | null;
  /** Pane only: the × button. */
  onClose?: () => void;
  /** Fired with the fresh case after any write, so the host list can update its row. */
  onChanged?: (c: QcCase) => void;
}

export function CaseView({ caseId, variant, seed = null, onClose, onChanged }: CaseViewProps) {
  const pane = variant === 'pane';
  const getToken = useApiToken();
  const me = useMe();
  const canManage = me?.canManage ?? false;
  const meOid = me?.oid ?? null;

  const [detail, setDetail] = useState<QcCaseDetail | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // What this browser had seen BEFORE this visit — captured once at mount so
  // the "new since your last visit" divider doesn't vanish when we mark the
  // case seen below.
  const [seenBefore] = useState<string | null>(() =>
    Number.isFinite(caseId) ? lastSeenAt(caseId) : null,
  );

  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');

  const [noteDraft, setNoteDraft] = useState('');
  const [noteFiles, setNoteFiles] = useState<PickedFile[]>([]);
  const [noteBusy, setNoteBusy] = useState(false);
  const [noteInternal, setNoteInternal] = useState(false);

  const [showIncidentForm, setShowIncidentForm] = useState(false);
  const [showMerge, setShowMerge] = useState(false);

  const [verifyBusy, setVerifyBusy] = useState(false);
  const [brokenOpen, setBrokenOpen] = useState(false);
  const [brokenComment, setBrokenComment] = useState('');

  const [decisionBusy, setDecisionBusy] = useState(false);
  const [acceptOpen, setAcceptOpen] = useState(false);
  const [acceptDirection, setAcceptDirection] = useState('');
  const [declineOpen, setDeclineOpen] = useState(false);
  const [declineComment, setDeclineComment] = useState('');

  const applyDetail = useCallback(
    (c: QcCaseDetail) => {
      setDetail(c);
      onChanged?.(c);
    },
    [onChanged],
  );

  const refresh = useCallback(async () => {
    try {
      const token = await getToken();
      const { case: c } = await api.getCase(token, caseId);
      setDetail(c);
      setLoadError(null);
      return c;
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load the case');
      return null;
    }
  }, [getToken, caseId]);

  useEffect(() => {
    if (!Number.isFinite(caseId)) {
      setLoadError('Invalid case id');
      return;
    }
    void refresh();
  }, [refresh, caseId]);

  // Record this case in the "recently viewed" quick-switch + mark it seen
  // (drives the unread markers on the list/dashboard) once loaded.
  useEffect(() => {
    if (detail) {
      logCaseView({ id: detail.id, caseNumber: detail.caseNumber, title: detail.title, status: detail.status });
      markCaseSeen(detail.id, detail.updatedAt);
      // The host's row carries updatedAt too — let it drop its unread dot.
      onChanged?.(detail);
    }
  }, [detail?.id, detail?.updatedAt]); // eslint-disable-line react-hooks/exhaustive-deps

  async function patchCase(patch: UpdateCasePatch) {
    setBusy(true);
    setActionError(null);
    try {
      const token = await getToken();
      const { case: c } = await api.updateCase(token, caseId, patch);
      applyDetail(c);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Update failed');
    } finally {
      setBusy(false);
    }
  }

  async function reopen() {
    setBusy(true);
    setActionError(null);
    try {
      const token = await getToken();
      const { case: c } = await api.reopenCase(token, caseId);
      applyDetail(c);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Reopen failed');
    } finally {
      setBusy(false);
    }
  }

  async function verify(works: boolean) {
    setVerifyBusy(true);
    setActionError(null);
    try {
      const token = await getToken();
      const { case: c } = await api.verifyCase(token, caseId, works, works ? undefined : brokenComment);
      applyDetail(c);
      setBrokenOpen(false);
      setBrokenComment('');
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Verification failed');
    } finally {
      setVerifyBusy(false);
    }
  }

  async function decide(accept: boolean) {
    setDecisionBusy(true);
    setActionError(null);
    try {
      const token = await getToken();
      const { case: c } = await api.decideCase(
        token,
        caseId,
        accept,
        accept ? undefined : declineComment,
        accept ? acceptDirection : undefined,
      );
      applyDetail(c);
      setAcceptOpen(false);
      setAcceptDirection('');
      setDeclineOpen(false);
      setDeclineComment('');
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Decision failed');
    } finally {
      setDecisionBusy(false);
    }
  }

  async function addNote() {
    const body = noteDraft.trim();
    if (!body && noteFiles.length === 0) return;
    setNoteBusy(true);
    setActionError(null);
    try {
      const token = await getToken();
      const attachmentUrls = noteFiles.length
        ? await uploadAttachments(token, noteFiles.map((f) => f.file))
        : [];
      await api.addNote(token, caseId, body, attachmentUrls, noteInternal ? 'internal' : 'public');
      setNoteDraft('');
      setNoteFiles([]);
      setNoteInternal(false);
      const fresh = await refresh();
      if (fresh) onChanged?.(fresh);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to add the note');
    } finally {
      setNoteBusy(false);
    }
  }

  // Paste an image directly into the reply box (QC-0032).
  function onPasteNoteImage(e: React.ClipboardEvent<HTMLTextAreaElement>) {
    const item = Array.from(e.clipboardData.items).find((i) => i.type.startsWith('image/'));
    if (!item) return;
    const pasted = item.getAsFile();
    if (!pasted) return;
    e.preventDefault();
    const ext = (pasted.type.split('/')[1] || 'png').replace('jpeg', 'jpg');
    const named = new File([pasted], `pasted-${Date.now()}.${ext}`, { type: pasted.type });
    setNoteFiles((prev) => appendFiles(prev, [named]));
  }

  async function openAttachment(attachmentUrl: string) {
    try {
      const token = await getToken();
      const { downloadUrl } = await api.attachmentDownloadUrl(token, attachmentUrl);
      window.open(downloadUrl, '_blank', 'noopener');
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to fetch the attachment');
    }
  }

  function attachmentLinks(urls: string[]) {
    if (urls.length === 0) return null;
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 8 }}>
        {urls.map((url, idx) => (
          <button key={url} type="button" onClick={() => void openAttachment(url)} style={attachmentLinkStyle}>
            {urls.length > 1 ? `Attachment ${idx + 1}` : 'View attachment'}
          </button>
        ))}
      </div>
    );
  }

  const reporterOids = useMemo(() => {
    const set = new Set<string>();
    if (detail?.openedBy) set.add(detail.openedBy.toLowerCase());
    for (const i of detail?.incidents ?? []) set.add(i.reporterId.toLowerCase());
    return set;
  }, [detail]);

  function roleOf(oid: string | null | undefined): 'reporter' | 'dev' {
    return oid && reporterOids.has(oid.toLowerCase()) ? 'reporter' : 'dev';
  }

  // === Header facts: from the detail when loaded, else from the seed row ===
  const head: QcCase | null = detail ?? seed;

  // Outer box: the pane is a flex column capped to the viewport so the header
  // and composer stay put while the thread scrolls; the page just flows.
  const outerStyle: React.CSSProperties = pane
    ? {
        ...cardStyle,
        padding: 0,
        display: 'flex',
        flexDirection: 'column',
        position: 'sticky',
        top: 12,
        maxHeight: 'calc(100vh - 136px)',
        minHeight: 420,
        overflow: 'hidden',
      }
    : { display: 'flex', flexDirection: 'column', gap: 12, padding: '24px 0' };

  if (loadError && !head) {
    return (
      <div style={pane ? { ...cardStyle } : { padding: '24px 0' }}>
        <div style={dangerTextStyle}>{loadError}</div>
        {!pane && (
          <p style={{ fontSize: 13 }}>
            <Link to="/cases">← Back to active cases</Link>
          </p>
        )}
      </div>
    );
  }

  if (!head) {
    return (
      <div style={pane ? { ...cardStyle, color: 'var(--brand-text-muted, #8BA4B8)', fontSize: 13 } : { padding: '24px 0', fontSize: 13, color: 'var(--brand-text-muted, #8BA4B8)' }}>
        Loading case…
      </div>
    );
  }

  const originalIncident = detail?.incidents.length
    ? detail.incidents.reduce((earliest, i) =>
        new Date(i.createdAt).getTime() < new Date(earliest.createdAt).getTime() ? i : earliest,
      )
    : undefined;
  const thread = detail ? buildThread(detail, originalIncident?.id) : [];
  const isTerminal = (QC_TERMINAL_STATUSES as readonly string[]).includes(head.status);
  const firstNewIndex =
    seenBefore && detail
      ? thread.findIndex((e) => new Date(e.at).getTime() > new Date(seenBefore).getTime())
      : -1;

  // Environment, said plainly: the single environment when every report
  // agrees, counts only when they disagree, "Not specified" otherwise.
  const envLabel = (() => {
    if (!detail) return null;
    const counts: Record<string, number> = {};
    for (const i of detail.incidents) {
      const k = i.environment ?? 'unspecified';
      counts[k] = (counts[k] ?? 0) + 1;
    }
    const named = Object.entries(counts).filter(([env]) => env !== 'unspecified');
    if (named.length === 0) return 'Not specified';
    if (named.length === 1 && detail.incidentCount === named[0][1]) return named[0][0];
    return named.map(([env, n]) => `${env} ×${n}`).join(', ');
  })();

  const composerEmpty = !noteDraft.trim() && noteFiles.length === 0;
  const people = detail?.people ?? {};
  const selfName = me?.displayName;

  // === Thread entry renderers ===
  function messageBlock(opts: {
    key: string;
    authorOid: string;
    via: string | null;
    at: string;
    body: string;
    attachmentUrls: string[];
    corroboration?: QcIncident;
    internal?: boolean;
  }) {
    const ai = isAiVia(opts.via);
    const role = ai ? 'dev' : roleOf(opts.authorOid);
    const dev = role === 'dev';
    const internal = !!opts.internal;
    return (
      <div
        key={opts.key}
        style={{
          borderLeft: `3px solid ${internal ? '#B45309' : ai ? '#7C3AED' : dev ? 'var(--brand-primary, #001C3E)' : 'var(--brand-border, #D6E4EE)'}`,
          background: internal ? '#FFFBEB' : dev ? 'var(--brand-bg, #F4F8FB)' : 'transparent',
          borderRadius: dev || internal ? '0 6px 6px 0' : 0,
          padding: '10px 14px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--brand-text-muted, #8BA4B8)', marginBottom: 4, flexWrap: 'wrap' }}>
          {ai && <AiTag />}
          <strong style={{ color: 'var(--brand-text, #001C3E)', fontWeight: 600 }}>
            {actorLabel(opts.authorOid, meOid, people, opts.via, { selfName })}
          </strong>
          <RoleTag role={role} />
          {internal && (
            <span
              title="Internal note — only the dev team (qc.manage) can see this; reporters cannot."
              style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#92400E', background: '#FDE68A', borderRadius: 6, padding: '1px 6px' }}
            >
              🔒 Internal
            </span>
          )}
          {opts.corroboration && <span style={{ fontSize: 11 }}>is seeing this too</span>}
          <span title={absoluteTime(opts.at)}>{relativeTime(opts.at)}</span>
          {opts.corroboration?.environment && (
            <span style={{ background: 'var(--brand-card-bg, white)', border: '1px solid var(--brand-border, #D6E4EE)', borderRadius: 4, padding: '0 6px', fontSize: 11 }}>
              {opts.corroboration.environment}
            </span>
          )}
          {opts.corroboration && opts.corroboration.source !== 'manual' && (
            <span style={{ fontSize: 11 }}>via {opts.corroboration.source}</span>
          )}
        </div>
        {opts.body && (
          <div style={{ fontSize: 13, color: 'var(--brand-text, #001C3E)', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{opts.body}</div>
        )}
        {attachmentLinks(opts.attachmentUrls)}
      </div>
    );
  }

  function systemLine(a: QcCaseActivity, key: string) {
    const d = a.eventData ?? {};
    const newStatus = a.eventType === 'status_changed' && typeof d.new === 'string' ? (d.new as QcCaseStatus) : null;
    let phrase: React.ReactNode;
    if (newStatus && STATUS_LABELS[newStatus]) {
      phrase = (
        <>
          moved this to{' '}
          <strong style={{ color: STATUS_STYLES[newStatus]?.fg ?? 'var(--brand-text, #001C3E)' }}>{STATUS_LABELS[newStatus]}</strong>
        </>
      );
    } else if (a.eventType === 'reopened') {
      phrase = 'reopened this case';
    } else if (a.eventType === 'merged') {
      const direction = typeof d.direction === 'string' ? d.direction : null;
      const other = typeof d.otherCaseId === 'number' ? d.otherCaseId : null;
      phrase = (
        <>
          {direction === 'merged_from' ? 'merged another case into this one' : 'merged this case'}
          {other ? (
            <>
              {' '}
              (<Link to={`/cases/${other}`}>case #{other}</Link>)
            </>
          ) : null}
        </>
      );
    } else if (a.eventType === 'archived') {
      phrase = 'archived this case';
    } else {
      phrase = a.eventType.replace(/_/g, ' ');
    }
    return (
      <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0 14px', fontSize: 12, color: 'var(--brand-text-muted, #8BA4B8)' }}>
        <span aria-hidden style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--brand-border, #D6E4EE)', flexShrink: 0 }} />
        <span>
          {isAiVia(a.via) && <AiTag />}
          <strong style={{ color: 'var(--brand-text, #001C3E)', fontWeight: 600 }}>
            {actorLabel(a.actorId, meOid, people, a.via, { selfName })}
          </strong>{' '}
          {phrase} <span title={absoluteTime(a.createdAt)}>· {relativeTime(a.createdAt)}</span>
        </span>
      </div>
    );
  }

  // === Header ===
  const header = (
    <div
      style={{
        padding: pane ? '12px 16px 10px' : '16px 20px',
        borderBottom: pane ? '1px solid var(--brand-border, #D6E4EE)' : undefined,
        background: 'var(--brand-card-bg, white)',
        ...(pane ? {} : { border: '1px solid var(--brand-border, #D6E4EE)', borderRadius: 8 }),
        flexShrink: 0,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
        <span style={{ fontFamily: MONO, fontSize: 12, color: 'var(--brand-text-muted, #8BA4B8)', whiteSpace: 'nowrap' }}>{head.caseNumber}</span>
        <TypeIcon type={head.type} />
        <StatusPill status={head.status} />
        <PriorityPill priority={head.priority} />
        <ModuleBadge slug={head.moduleSlug} name={head.moduleName} />
        {head.submoduleName && <span style={{ fontSize: 12, color: 'var(--brand-text-muted, #8BA4B8)' }}>/ {head.submoduleName}</span>}
        {detail?.archivedAt && (
          <span style={{ fontSize: 11, color: '#791F1F' }} title={absoluteTime(detail.archivedAt)}>
            Archived {relativeTime(detail.archivedAt)}
          </span>
        )}
        <span style={{ flex: 1 }} />
        {pane && (
          <>
            <Link
              to={`/cases/${caseId}`}
              title="Open this case on its own page"
              style={{ fontSize: 12, color: 'var(--brand-text-muted, #5A6B7B)', textDecoration: 'none', whiteSpace: 'nowrap' }}
            >
              Open page ↗
            </Link>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close case"
              title="Close (Esc)"
              style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 18, lineHeight: 1, color: 'var(--brand-text-muted, #8BA4B8)', padding: '0 2px' }}
            >
              ×
            </button>
          </>
        )}
      </div>

      {editingTitle ? (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            autoFocus
            type="text"
            value={titleDraft}
            maxLength={256}
            onChange={(e) => setTitleDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && titleDraft.trim()) {
                setEditingTitle(false);
                void patchCase({ title: titleDraft.trim() });
              }
              if (e.key === 'Escape') setEditingTitle(false);
            }}
            style={{ ...inputStyle, fontSize: 15, fontWeight: 600, flex: 1 }}
          />
          <button
            type="button"
            disabled={!titleDraft.trim() || busy}
            onClick={() => {
              setEditingTitle(false);
              void patchCase({ title: titleDraft.trim() });
            }}
            style={primaryButtonStyle}
          >
            Save
          </button>
          <button type="button" onClick={() => setEditingTitle(false)} style={secondaryButtonStyle}>
            Cancel
          </button>
        </div>
      ) : (
        <h2 style={{ margin: 0, fontSize: pane ? 16 : 20, fontWeight: 600, lineHeight: 1.3, color: 'var(--brand-text, #001C3E)' }}>
          {head.title}
          {canManage && (
            <button
              type="button"
              onClick={() => {
                setTitleDraft(head.title);
                setEditingTitle(true);
              }}
              title="Edit title"
              style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 12, color: 'var(--brand-text-muted, #8BA4B8)', marginLeft: 8, verticalAlign: 'middle' }}
            >
              ✎
            </button>
          )}
        </h2>
      )}

      {/* The facts that used to live in a right-hand "Details" card. */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 14px', fontSize: 12, color: 'var(--brand-text-muted, #5A6B7B)', marginTop: 6 }}>
        <span>
          Opened by <strong style={{ color: 'var(--brand-text, #001C3E)', fontWeight: 600 }}>{personLabel(head.openedBy, meOid, people, { selfName })}</strong>{' '}
          <span title={absoluteTime(head.createdAt)}>{relativeTime(head.createdAt)}</span>
        </span>
        <span>
          Assigned to{' '}
          <strong style={{ color: 'var(--brand-text, #001C3E)', fontWeight: 600 }}>
            {head.assignedTo ? personLabel(head.assignedTo, meOid, people, { selfName }) : 'nobody'}
          </strong>
        </span>
        <span>
          {head.incidentCount} report{head.incidentCount === 1 ? '' : 's'}
        </span>
        {envLabel && <span>Environment {envLabel}</span>}
        {detail?.closedAt && (
          <span>
            Closed <span title={absoluteTime(detail.closedAt)}>{relativeTime(detail.closedAt)}</span>
          </span>
        )}
        {detail?.duplicateOfCaseId && (
          <span>
            Duplicate of <Link to={`/cases/${detail.duplicateOfCaseId}`}>case #{detail.duplicateOfCaseId}</Link>
          </span>
        )}
      </div>

      {/* Triage strip (qc.manage) — the controls that used to be a side card. */}
      {canManage && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
          <select
            value={head.status}
            disabled={busy || !detail}
            onChange={(e) => void patchCase({ status: e.target.value as QcCaseStatus })}
            title="Status"
            aria-label="Status"
            style={compactSelectStyle}
          >
            {QC_CASE_STATUSES.map((s) => (
              <option key={s} value={s}>
                {STATUS_LABELS[s]}
              </option>
            ))}
          </select>
          <select
            value={head.priority}
            disabled={busy || !detail}
            onChange={(e) => void patchCase({ priority: e.target.value as QcCasePriority })}
            title="Priority"
            aria-label="Priority"
            style={compactSelectStyle}
          >
            {QC_CASE_PRIORITIES.map((p) => (
              <option key={p} value={p}>
                {p === 'high' ? 'High priority' : p === 'medium' ? 'Medium priority' : 'Low priority'}
              </option>
            ))}
          </select>
          <select
            value={head.type}
            disabled={busy || !detail}
            onChange={(e) => void patchCase({ type: e.target.value as QcCaseType })}
            title="Type"
            aria-label="Type"
            style={compactSelectStyle}
          >
            {QC_CASE_TYPES.map((t) => (
              <option key={t} value={t}>
                {t === 'bug' ? '🐛 Bug' : '✨ Feature request'}
              </option>
            ))}
          </select>
          {meOid && head.assignedTo?.toLowerCase() !== meOid.toLowerCase() && (
            <button type="button" disabled={busy || !detail} onClick={() => void patchCase({ assignedTo: meOid })} style={quietButtonStyle}>
              Assign to me
            </button>
          )}
          {head.assignedTo && (
            <button type="button" disabled={busy || !detail} onClick={() => void patchCase({ assignedTo: null })} style={quietButtonStyle}>
              Unassign
            </button>
          )}
          <button type="button" disabled={busy || !detail} onClick={() => setShowMerge(true)} style={quietButtonStyle}>
            Merge into…
          </button>
          {(isTerminal || detail?.archivedAt) && (
            <button type="button" disabled={busy || !detail} onClick={() => void reopen()} style={quietButtonStyle}>
              Reopen
            </button>
          )}
        </div>
      )}
      {actionError && <div style={{ ...dangerTextStyle, marginTop: 8 }}>{actionError}</div>}
      {loadError && <div style={{ ...dangerTextStyle, marginTop: 8 }}>{loadError}</div>}
    </div>
  );

  // === Decision + verify banners ===
  const decisionBanner =
    canManage && detail && detail.type === 'feature_request' && (detail.status === 'new' || detail.status === 'triaged') ? (
      <div style={{ ...cardStyle, background: '#F2F7FD', border: '1px solid #A9C8E8', padding: '12px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--brand-primary, #001C3E)', marginBottom: 2 }}>This feature request needs a decision.</div>
            <div style={{ fontSize: 12, color: 'var(--brand-text-muted, #5A6B7B)' }}>Accept puts it on the roadmap and tells the reporter. Decline closes it with your reasoning.</div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              type="button"
              disabled={decisionBusy}
              onClick={() => {
                setAcceptOpen((v) => !v);
                setDeclineOpen(false);
              }}
              style={{ ...primaryButtonStyle, opacity: decisionBusy ? 0.6 : 1 }}
            >
              ✓ Accept
            </button>
            <button
              type="button"
              disabled={decisionBusy}
              onClick={() => {
                setDeclineOpen((v) => !v);
                setAcceptOpen(false);
              }}
              style={quietButtonStyle}
            >
              ✗ Decline
            </button>
          </div>
        </div>
        {acceptOpen && (
          <div style={{ marginTop: 12 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#92400E', marginBottom: 4 }}>
              🔒 Direction for the dev team (internal — the reporter never sees this)
            </label>
            <textarea
              autoFocus
              value={acceptDirection}
              onChange={(e) => setAcceptDirection(e.target.value)}
              placeholder="Optional build direction — scope, constraints, how you want it done…"
              disabled={decisionBusy}
              style={{ ...textareaStyle, minHeight: 56, marginBottom: 8, background: '#FFFBEB', border: '1px solid #FDE68A' }}
            />
            <button type="button" disabled={decisionBusy} onClick={() => void decide(true)} style={{ ...primaryButtonStyle, opacity: decisionBusy ? 0.6 : 1 }}>
              {decisionBusy ? 'Accepting…' : 'Confirm acceptance'}
            </button>
          </div>
        )}
        {declineOpen && (
          <div style={{ marginTop: 12 }}>
            <textarea
              autoFocus
              value={declineComment}
              onChange={(e) => setDeclineComment(e.target.value)}
              placeholder="Why not? (optional — the reporter sees this)"
              disabled={decisionBusy}
              style={{ ...textareaStyle, minHeight: 56, marginBottom: 8 }}
            />
            <button type="button" disabled={decisionBusy} onClick={() => void decide(false)} style={{ ...primaryButtonStyle, opacity: decisionBusy ? 0.6 : 1 }}>
              {decisionBusy ? 'Declining…' : 'Decline this request'}
            </button>
          </div>
        )}
      </div>
    ) : null;

  const verifyBanner =
    detail && detail.status === 'fixed' && meOid && reporterOids.has(meOid.toLowerCase()) ? (
      <div style={{ ...cardStyle, background: '#F0FAF4', border: '1px solid #9BD4AE', padding: '12px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#14532D', marginBottom: 2 }}>The dev team marked this as fixed.</div>
            <div style={{ fontSize: 12, color: '#166534' }}>Does it work for you now? Your answer closes the loop.</div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button type="button" disabled={verifyBusy} onClick={() => void verify(true)} style={{ ...primaryButtonStyle, background: '#1B7A3D', opacity: verifyBusy ? 0.6 : 1 }}>
              ✓ Works — close it
            </button>
            <button type="button" disabled={verifyBusy} onClick={() => setBrokenOpen((v) => !v)} style={quietButtonStyle}>
              ✗ Still broken
            </button>
          </div>
        </div>
        {brokenOpen && (
          <div style={{ marginTop: 12 }}>
            <textarea
              autoFocus
              value={brokenComment}
              onChange={(e) => setBrokenComment(e.target.value)}
              placeholder="What's still not working? (optional, but it helps)"
              disabled={verifyBusy}
              style={{ ...textareaStyle, minHeight: 56, marginBottom: 8 }}
            />
            <button type="button" disabled={verifyBusy} onClick={() => void verify(false)} style={{ ...primaryButtonStyle, opacity: verifyBusy ? 0.6 : 1 }}>
              {verifyBusy ? 'Sending…' : 'Send — reopen for the dev team'}
            </button>
          </div>
        )}
      </div>
    ) : null;

  // === The thread (scrolls inside the pane) ===
  const body = (
    <div style={pane ? { flex: 1, minHeight: 0, overflowY: 'auto', padding: 12, display: 'flex', flexDirection: 'column', gap: 12 } : { display: 'flex', flexDirection: 'column', gap: 12 }}>
      {decisionBanner}
      {verifyBanner}

      {/* Pinned original report — paints from the row's description-less
          seed as a placeholder until the detail lands. */}
      <div style={{ ...cardStyle, background: 'var(--brand-bg, #F4F8FB)', padding: '12px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 6 }}>
          <h3 style={{ ...sectionHeadStyle, margin: 0 }}>Original report</h3>
          <button type="button" disabled={!detail} onClick={() => setShowIncidentForm(true)} style={quietButtonStyle}>
            I'm seeing this too
          </button>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--brand-text-muted, #8BA4B8)', marginBottom: 6, flexWrap: 'wrap' }}>
          <strong style={{ color: 'var(--brand-text, #001C3E)', fontWeight: 600 }}>{personLabel(head.openedBy, meOid, people, { selfName })}</strong>
          <RoleTag role="reporter" />
          <span title={absoluteTime(head.createdAt)}>reported {relativeTime(head.createdAt)}</span>
          {originalIncident?.environment && (
            <span style={{ background: 'var(--brand-card-bg, white)', border: '1px solid var(--brand-border, #D6E4EE)', borderRadius: 4, padding: '0 6px', fontSize: 11 }}>
              {originalIncident.environment}
            </span>
          )}
          {originalIncident && originalIncident.source !== 'manual' && <span style={{ fontSize: 11 }}>via {originalIncident.source}</span>}
        </div>
        {detail ? (
          <div style={{ fontSize: 13, color: 'var(--brand-text, #001C3E)', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{detail.description}</div>
        ) : (
          <div aria-hidden style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ height: 10, width: '92%', borderRadius: 4, background: 'var(--brand-border, #D6E4EE)', opacity: 0.6 }} />
            <span style={{ height: 10, width: '74%', borderRadius: 4, background: 'var(--brand-border, #D6E4EE)', opacity: 0.6 }} />
          </div>
        )}
        {originalIncident && attachmentLinks(originalIncident.attachmentUrls)}
      </div>

      <div style={{ ...cardStyle, padding: '12px 6px' }}>
        {!detail ? (
          <div style={{ fontSize: 12, color: 'var(--brand-text-muted, #8BA4B8)', padding: '2px 14px' }}>Loading the conversation…</div>
        ) : thread.length === 0 ? (
          <div style={{ fontSize: 13, color: 'var(--brand-text-muted, #8BA4B8)', padding: '2px 14px' }}>
            No replies yet. Your reply below starts the conversation with the reporter.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {thread.map((entry, idx) => {
              const parts: React.ReactNode[] = [];
              if (idx === firstNewIndex) {
                parts.push(
                  <div key="new-divider" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '0 8px' }}>
                    <span style={{ flex: 1, height: 1, background: '#B45309', opacity: 0.4 }} />
                    <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#B45309' }}>New since your last visit</span>
                    <span style={{ flex: 1, height: 1, background: '#B45309', opacity: 0.4 }} />
                  </div>,
                );
              }
              if (entry.kind === 'note') {
                parts.push(
                  messageBlock({
                    key: `n-${entry.note.id}`,
                    authorOid: entry.note.authorId,
                    via: entry.note.via,
                    at: entry.note.createdAt,
                    body: entry.note.body,
                    attachmentUrls: entry.note.attachmentUrls,
                    internal: entry.note.visibility === 'internal',
                  }),
                );
              } else if (entry.kind === 'incident') {
                parts.push(
                  messageBlock({
                    key: `i-${entry.incident.id}`,
                    authorOid: entry.incident.reporterId,
                    via: null,
                    at: entry.incident.createdAt,
                    body: entry.incident.description,
                    attachmentUrls: entry.incident.attachmentUrls,
                    corroboration: entry.incident,
                  }),
                );
              } else if (entry.kind === 'system') {
                parts.push(systemLine(entry.activity, `s-${entry.activity.id}`));
              } else {
                parts.push(
                  <ChangesGroup
                    key={`c-${entry.items[0].id}`}
                    items={entry.items}
                    renderLine={(a) => (
                      <span>
                        {isAiVia(a.via) && <AiTag />}
                        <strong style={{ color: 'var(--brand-text, #001C3E)', fontWeight: 600 }}>
                          {actorLabel(a.actorId, meOid, people, a.via, { selfName })}
                        </strong>{' '}
                        {minorChangeLine(a)} <span title={absoluteTime(a.createdAt)}>· {relativeTime(a.createdAt)}</span>
                      </span>
                    )}
                  />,
                );
              }
              return parts;
            })}
          </div>
        )}
      </div>
    </div>
  );

  // === Composer: pinned to the bottom of the pane / sticky on the page ===
  const composer = (
    <div
      style={{
        ...cardStyle,
        padding: '10px 14px',
        flexShrink: 0,
        ...(pane
          ? { border: 'none', borderTop: '1px solid var(--brand-border, #D6E4EE)', borderRadius: 0 }
          : { position: 'sticky', bottom: 12, boxShadow: '0 6px 20px rgba(0, 28, 62, 0.10)' }),
        ...(noteInternal ? { background: '#FFFBEB' } : {}),
      }}
    >
      <textarea
        value={noteDraft}
        onChange={(e) => setNoteDraft(e.target.value)}
        onPaste={onPasteNoteImage}
        onKeyDown={(e) => {
          // Ctrl/Cmd+Enter sends — the modern reply convention.
          if ((e.ctrlKey || e.metaKey) && e.key === 'Enter' && !composerEmpty && !noteBusy) {
            e.preventDefault();
            void addNote();
          }
        }}
        placeholder={noteInternal ? 'Internal note — only the dev team will see this…' : 'Reply to this case… (paste a screenshot to attach it)'}
        disabled={noteBusy || !detail}
        style={{ ...textareaStyle, minHeight: 52, marginBottom: 8 }}
      />
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <AttachmentPicker files={noteFiles} setFiles={setNoteFiles} disabled={noteBusy} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
          {canManage && (
            <label
              title="Internal notes are visible to the dev team only — reporters never see them and are not emailed."
              style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: noteInternal ? 700 : 500, color: noteInternal ? '#92400E' : 'var(--brand-text-muted, #8BA4B8)', cursor: 'pointer', userSelect: 'none' }}
            >
              <input type="checkbox" checked={noteInternal} onChange={(e) => setNoteInternal(e.target.checked)} disabled={noteBusy} style={{ accentColor: '#B45309', cursor: 'pointer' }} />
              🔒 Internal
            </label>
          )}
          <button
            type="button"
            onClick={() => void addNote()}
            disabled={noteBusy || composerEmpty}
            title="Ctrl+Enter"
            style={{ ...primaryButtonStyle, ...(noteInternal ? { background: '#B45309' } : {}), opacity: noteBusy || composerEmpty ? 0.6 : 1, flexShrink: 0 }}
          >
            {noteBusy ? 'Saving…' : noteInternal ? 'Add internal note' : 'Reply'}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div style={outerStyle} data-case-view={variant}>
      {!pane && (
        <p style={{ margin: '0 0 4px', fontSize: 12 }}>
          <Link to="/cases" style={{ color: 'var(--brand-text-muted, #8BA4B8)' }}>
            ← Active cases
          </Link>
        </p>
      )}
      {header}
      {body}
      {composer}

      {showIncidentForm && detail && (
        <Modal title={`I'm seeing this too — ${detail.caseNumber}`} onClose={() => setShowIncidentForm(false)} maxWidth={560}>
          <IncidentForm
            caseId={caseId}
            onDone={() => {
              setShowIncidentForm(false);
              void refresh().then((c) => c && onChanged?.(c));
            }}
          />
        </Modal>
      )}

      {showMerge && detail && (
        <MergeModal
          source={detail}
          onClose={() => setShowMerge(false)}
          onMerged={() => {
            setShowMerge(false);
            void refresh().then((c) => c && onChanged?.(c));
          }}
        />
      )}
    </div>
  );
}

// A run of minor bookkeeping events, collapsed to one quiet row.
function ChangesGroup({ items, renderLine }: { items: QcCaseActivity[]; renderLine: (a: QcCaseActivity) => React.ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ padding: '0 14px', fontSize: 12, color: 'var(--brand-text-muted, #8BA4B8)' }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', fontSize: 12, color: 'var(--brand-text-muted, #8BA4B8)', display: 'flex', alignItems: 'center', gap: 8 }}
      >
        <span aria-hidden style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--brand-border, #D6E4EE)', flexShrink: 0 }} />
        <span style={{ textDecoration: 'underline dotted' }}>
          {open ? '▾' : '▸'} {items.length === 1 ? '1 minor change' : `${items.length} minor changes`}
        </span>
      </button>
      {open && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, margin: '6px 0 0 15px' }}>
          {items.map((a) => (
            <div key={a.id}>{renderLine(a)}</div>
          ))}
        </div>
      )}
    </div>
  );
}

// === Merge UI: search-as-you-type for the target case + confirm step ===
function MergeModal({ source, onClose, onMerged }: { source: QcCaseDetail; onClose: () => void; onMerged: () => void }) {
  const getToken = useApiToken();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<QcCase[]>([]);
  const [searching, setSearching] = useState(false);
  const [target, setTarget] = useState<QcCase | null>(null);
  const [merging, setMerging] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      return;
    }
    const handle = window.setTimeout(() => {
      setSearching(true);
      void (async () => {
        try {
          const token = await getToken();
          const res = await api.listCases(token, { search: q, pageSize: 8 });
          setResults(res.cases.filter((c) => c.id !== source.id));
        } catch {
          setResults([]);
        } finally {
          setSearching(false);
        }
      })();
    }, 300);
    return () => window.clearTimeout(handle);
  }, [query, getToken, source.id]);

  async function confirmMerge() {
    if (!target) return;
    setMerging(true);
    setError(null);
    try {
      const token = await getToken();
      await api.mergeCase(token, source.id, target.id);
      onMerged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Merge failed');
      setMerging(false);
    }
  }

  if (target) {
    return (
      <Modal title="Confirm merge" onClose={onClose} maxWidth={520}>
        <div style={{ fontSize: 13, color: 'var(--brand-text, #001C3E)', marginBottom: 16, lineHeight: 1.6 }}>
          <p style={{ margin: '0 0 10px' }}>
            <span style={{ fontFamily: MONO, fontSize: 12 }}>{source.caseNumber}</span> <strong>{source.title}</strong> will be marked as a <strong>duplicate</strong> of{' '}
            <span style={{ fontFamily: MONO, fontSize: 12 }}>{target.caseNumber}</span> <strong>{target.title}</strong>.
          </p>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            <li>
              {source.incidentCount} incident{source.incidentCount === 1 ? '' : 's'} will move to {target.caseNumber}
            </li>
            <li>This case's status becomes "Duplicate" (terminal — auto-archives in 30 days)</li>
          </ul>
        </div>
        {error && <div style={{ ...dangerTextStyle, marginBottom: 12 }}>{error}</div>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" onClick={() => setTarget(null)} style={secondaryButtonStyle}>
            Back
          </button>
          <button type="button" onClick={() => void confirmMerge()} disabled={merging} style={{ ...primaryButtonStyle, opacity: merging ? 0.6 : 1 }}>
            {merging ? 'Merging…' : 'Merge cases'}
          </button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal title={`Merge ${source.caseNumber} into…`} onClose={onClose} maxWidth={560}>
      <input
        autoFocus
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search for the case to keep (title or case number)…"
        style={{ ...inputStyle, marginBottom: 12 }}
      />
      {searching && <div style={{ fontSize: 12, color: 'var(--brand-text-muted, #8BA4B8)' }}>Searching…</div>}
      {!searching && query.trim().length >= 2 && results.length === 0 && (
        <div style={{ fontSize: 12, color: 'var(--brand-text-muted, #8BA4B8)' }}>No matching cases.</div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {results.map((c) => (
          <button
            key={c.id}
            type="button"
            onClick={() => setTarget(c)}
            style={{ textAlign: 'left', background: 'var(--brand-card-bg, white)', border: '1px solid var(--brand-border, #D6E4EE)', borderRadius: 6, padding: '10px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10 }}
          >
            <span style={{ fontFamily: MONO, fontSize: 11, color: 'var(--brand-text-muted, #8BA4B8)' }}>{c.caseNumber}</span>
            <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--brand-text, #001C3E)', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.title}</span>
            <StatusPill status={c.status} />
          </button>
        ))}
      </div>
    </Modal>
  );
}
